// Central map catalogue with size-class metadata so team-size selection
// can filter the picker automatically. `size` is a set of team-size ids the
// map is suitable for. Add new maps here — nothing else needs to change.
import { Target, Warehouse, Sun, Wrench, Mountain, Landmark, Building, Flame } from "lucide-react";

export const TEAM_SIZE_CATALOG = [
    { id: "1v1",   label: "1 v 1",  desc: "Duel",        maxPlayers: 2,  perfCost: "low"   },
    { id: "2v2",   label: "2 v 2",  desc: "Pair",        maxPlayers: 4,  perfCost: "low"   },
    { id: "3v3",   label: "3 v 3",  desc: "Squad",       maxPlayers: 6,  perfCost: "low"   },
    { id: "5v5",   label: "5 v 5",  desc: "Standard",    maxPlayers: 10, perfCost: "med"   },
    { id: "10v10", label: "10 v 10", desc: "Large team",  maxPlayers: 20, perfCost: "high"  },
    { id: "15v15", label: "15 v 15", desc: "Battle",      maxPlayers: 30, perfCost: "high"  },
    { id: "30v30", label: "30 v 30", desc: "Experimental",maxPlayers: 60, perfCost: "extreme"},
];

// The four maps currently in-engine, plus 4 placeholders reserved for the
// larger team sizes so the picker always has options.
export const MAPS = [
    {
        id: "training_yard",
        name: "Training Yard",
        subtitle: "OUTDOOR · CQC",
        icon: Target,
        playable: true,
        tint: "#4a5b6b",
        sizes: ["1v1", "2v2"],
        status: "PLAYABLE",
        preview: {
            environment: "OUTDOOR COMPOUND",
            recommendedPlayers: "2 – 4",
            difficulty: "BEGINNER",
            palette: ["#8b8b83", "#7d3b2a", "#3a3c40"],
            tagline: "Practice grounds with clean sightlines and simple cover.",
        },
    },
    {
        id: "factory",
        name: "Factory",
        subtitle: "INDUSTRIAL",
        icon: Wrench,
        playable: true,
        tint: "#6b4a4a",
        sizes: ["2v2", "3v3"],
        status: "PLAYABLE",
        preview: {
            environment: "INDUSTRIAL COMPLEX",
            recommendedPlayers: "4 – 6",
            difficulty: "INTERMEDIATE",
            palette: ["#6b4a34", "#3a3c40", "#c58f2a"],
            tagline: "Overhead pipes, red containers and a long conveyor spine.",
        },
    },
    {
        id: "warehouse",
        name: "Warehouse",
        subtitle: "INDOOR CONTROL",
        icon: Warehouse,
        playable: true,
        tint: "#4a6b5a",
        sizes: ["3v3", "5v5"],
        status: "PLAYABLE",
        preview: {
            environment: "INDOOR STORAGE",
            recommendedPlayers: "6 – 10",
            difficulty: "INTERMEDIATE",
            palette: ["#4a6b5a", "#3a3c40", "#a67a44"],
            tagline: "Tall shelving lanes, loading dock, close-in warehouse combat.",
        },
    },
    {
        id: "desert_base",
        name: "Desert Base",
        subtitle: "OPEN · LONG RANGE",
        icon: Sun,
        playable: true,
        tint: "#6b634a",
        sizes: ["5v5", "10v10"],
        status: "PLAYABLE",
        preview: {
            environment: "DESERT OUTPOST",
            recommendedPlayers: "6 – 10",
            difficulty: "ADVANCED",
            palette: ["#c8b075", "#6b3a26", "#7a6a45"],
            tagline: "Open sand, long sight-lines and sandbag bunkers.",
        },
    },
    {
        id: "highlands",
        name: "Highlands",
        subtitle: "MOUNTAIN · LONG SIGHTLINES",
        icon: Mountain,
        playable: false,
        tint: "#5a6b60",
        sizes: ["10v10", "15v15"],
        status: "COMING SOON",
        preview: {
            environment: "MOUNTAIN RESEARCH BASE",
            recommendedPlayers: "20 – 30",
            difficulty: "ADVANCED",
            palette: ["#5a6b60", "#e6e9ef", "#3b2a1a"],
            tagline: "Snow-blanketed alpine ridge above a research compound.",
        },
    },
    {
        id: "downtown",
        name: "Downtown",
        subtitle: "URBAN · MULTI-STOREY",
        icon: Building,
        playable: false,
        tint: "#5a5a6b",
        sizes: ["10v10", "15v15", "30v30"],
        status: "COMING SOON",
        preview: {
            environment: "MODERN OFFICE DISTRICT",
            recommendedPlayers: "20 – 60",
            difficulty: "ADVANCED",
            palette: ["#5a5a6b", "#a4d9e6", "#8b8b83"],
            tagline: "Glass towers, back alleys and a rooftop skyline.",
        },
    },
    {
        id: "citadel",
        name: "Citadel",
        subtitle: "FORTRESS · SIEGE LAYOUT",
        icon: Landmark,
        playable: false,
        tint: "#4a4a4a",
        sizes: ["15v15", "30v30"],
        status: "COMING SOON",
        preview: {
            environment: "ABANDONED FORTRESS",
            recommendedPlayers: "30 – 60",
            difficulty: "ELITE",
            palette: ["#4a4a4a", "#7d3b2a", "#5a3b25"],
            tagline: "Ramparts, tunnels and a central keep. Layered defence.",
        },
    },
    {
        id: "wildfire_ridge",
        name: "Wildfire Ridge",
        subtitle: "OUTDOOR · WEATHER",
        icon: Flame,
        playable: false,
        tint: "#6b4a34",
        sizes: ["30v30"],
        status: "EXPERIMENTAL",
        preview: {
            environment: "BURNING WILDLANDS",
            recommendedPlayers: "60",
            difficulty: "ELITE",
            palette: ["#6b4a34", "#ff5040", "#3c5f3a"],
            tagline: "Rolling terrain, ember storms, dynamic fire lines.",
        },
    },
];

export function mapsForTeamSize(teamSizeId) {
    return MAPS.filter((m) => m.sizes.includes(teamSizeId));
}

export const WEATHER = [
    { id: "sunny",      label: "Sunny",      description: "Clear sky · full sun" },
    { id: "cloudy",     label: "Cloudy",     description: "Overcast · soft light" },
    { id: "rain",       label: "Rain",       description: "Steady rainfall" },
    { id: "heavy_rain", label: "Heavy Rain", description: "Storm · low visibility" },
    { id: "snow",       label: "Snow",       description: "Slow falling flakes" },
    { id: "fog",        label: "Fog",        description: "Very short sightlines" },
];

export const TIME_OF_DAY = [
    { id: "morning", label: "Morning", description: "Soft golden hour" },
    { id: "day",     label: "Day",     description: "Bright and clear" },
    { id: "sunset",  label: "Sunset",  description: "Warm orange light" },
    { id: "evening", label: "Evening", description: "Dimmer · artificial lights" },
    { id: "night",   label: "Night",   description: "Street lamps · deep shadows" },
];

export const ROUND_COUNTS = [
    { id: 3,  label: "Best of 5",  description: "First to 3" },
    { id: 5,  label: "Best of 9",  description: "First to 5" },
    { id: 7,  label: "Best of 13", description: "First to 7" },
];

export const TIME_LIMITS = [
    { id: 300,  label: "5 min",  description: "Fast" },
    { id: 600,  label: "10 min", description: "Standard" },
    { id: 900,  label: "15 min", description: "Extended" },
    { id: 1200, label: "20 min", description: "Marathon" },
];

export const SCORE_LIMITS = [
    { id: 25,   label: "25 pts",  description: "Sprint" },
    { id: 50,   label: "50 pts",  description: "Standard" },
    { id: 100,  label: "100 pts", description: "Long" },
    { id: 0,    label: "Timer only", description: "No score cap" },
];

export function getMapById(id) {
    return MAPS.find((m) => m.id === id) || MAPS[0];
}
